import { useEffect, type RefObject } from 'react';

/**
 * Size a media element to the largest 16:9 rectangle that fits in `frameRef`'s
 * content box, then let flexbox center it. More reliable than container queries
 * when the frame height was historically collapsing on iOS Safari.
 *
 * `layoutKey` — bump when padding/alignment changes without a border-box resize
 * (e.g. compose open), so we remeasure the content box.
 */
export function useContain16x9(
  frameRef: RefObject<HTMLElement | null>,
  mediaRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  layoutKey: unknown = 0,
): void {
  useEffect(() => {
    if (!enabled) return undefined;
    const frame = frameRef.current;
    const media = mediaRef.current;
    if (!frame || !media) return undefined;

    const sync = () => {
      const style = getComputedStyle(frame);
      const padX =
        (Number.parseFloat(style.paddingLeft) || 0) +
        (Number.parseFloat(style.paddingRight) || 0);
      const padY =
        (Number.parseFloat(style.paddingTop) || 0) +
        (Number.parseFloat(style.paddingBottom) || 0);
      const fw = frame.clientWidth - padX;
      const fh = frame.clientHeight - padY;
      if (fw <= 1 || fh <= 1) return;

      let width = fw;
      let height = (width * 9) / 16;
      if (height > fh) {
        height = fh;
        width = (height * 16) / 9;
      }

      media.style.width = `${Math.round(width)}px`;
      media.style.height = `${Math.round(height)}px`;
    };

    sync();
    const raf = window.requestAnimationFrame(sync);
    const ro = new ResizeObserver(sync);
    ro.observe(frame);
    // Shell / stage size changes may not resize the absolute frame's border box
    // in the same tick — also watch the video host when present.
    const host = frame.parentElement;
    if (host) ro.observe(host);
    window.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('scroll', sync);

    return () => {
      window.cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('scroll', sync);
      media.style.width = '';
      media.style.height = '';
    };
  }, [enabled, frameRef, mediaRef, layoutKey]);
}
